import { useState, useEffect } from "react"
import { X, Moon, Sun, Bell, Lock, Palette, Volume2 } from "lucide-react"
import { defaultSettings, saveSettings, loadSettings } from "./../../lib/settings"
// import { Switch } from "@/components/ui/switch"
import {Switch} from "../ui/switch";
// import { Label } from "@/components/ui/label"
import {Label} from "../ui/label";

// import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { RadioGroup, RadioGroupItem } from "../ui/radio-group"

// import { Slider } from "@/components/ui/slider"
import { Slider } from "../ui/slider"

// import { playSound } from "@/lib/sounds"
import { cn } from "../../lib/utils"

export function SettingsModal({ isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState("appearance")
  const [settings, setSettings] = useState(defaultSettings)
  const [isDark, setIsDark] = useState(false)
  const [isMobileView, setIsMobileView] = useState(false)

  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 768)
    }

    checkMobileView()
    window.addEventListener("resize", checkMobileView)
    return () => window.removeEventListener("resize", checkMobileView)
  }, [])

  useEffect(() => {
    const savedSettings = loadSettings()
    setSettings(savedSettings)
    const isDarkTheme =
      savedSettings.theme === "dark" ||
      (savedSettings.theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches)
    setIsDark(isDarkTheme)
  }, [])

  const toggleTheme = () => {
    const newTheme = isDark ? "light" : "dark"
    setIsDark(!isDark)
    const newSettings = {
      ...settings,
      theme: newTheme
    }
    setSettings(newSettings)
    saveSettings(newSettings)
    document.documentElement.classList.toggle("dark", !isDark)
  }

  const updateSettings = (newSettings) => {
    const updatedSettings = { ...settings, ...newSettings }
    setSettings(updatedSettings)
    saveSettings(updatedSettings)

    if (newSettings.sound?.messageSound || newSettings.sound?.notificationSound || newSettings.sound?.callSound) {
      // playSound("notification")
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center bg-black/50 p-0 md:p-4">
      <div
        className={cn(
          "settings-modal bg-white dark:bg-gray-900 w-full md:w-[42rem] h-full md:h-auto md:max-h-[80vh] overflow-hidden",
          "md:rounded-xl md:shadow-xl flex flex-col",
        )}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-xl font-bold dark:text-white">Settings</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div
          className={cn(
            "flex flex-col md:flex-row h-[calc(100%-4rem)] md:h-[calc(80vh-4rem)]",
            isMobileView && activeTab !== "appearance" ? "hidden" : "flex",
          )}
        >
          <div className="w-full md:w-64 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-800 p-4">
            <nav className="space-y-1">
              {[
                { id: "appearance", icon: Palette, label: "Appearance" },
                { id: "notifications", icon: Bell, label: "Notifications" },
                { id: "sound", icon: Volume2, label: "Sound" },
                { id: "privacy", icon: Lock, label: "Privacy" },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors",
                    activeTab === item.id
                      ? "bg-purple-50 dark:bg-gray-800 text-purple-500 dark:text-purple-400"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300",
                  )}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            {isMobileView && activeTab !== "appearance" && (
              <button
                onClick={() => setActiveTab("appearance")}
                className="mb-4 flex items-center text-sm text-gray-500 dark:text-gray-400"
              >
                <X className="w-4 h-4 mr-1" /> Back
              </button>
            )}

            {activeTab === "appearance" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-4 dark:text-white">Theme</h3>
                  <button
                    onClick={toggleTheme}
                    className="w-full h-12 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center px-4 gap-4"
                  >
                    {isDark ? (
                      <>
                        <Sun className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                        <span className="dark:text-white">Light Mode</span>
                      </>
                    ) : (
                      <>
                        <Moon className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                        <span>Dark Mode</span>
                      </>
                    )}
                  </button>
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-4 dark:text-white">Chat Density</h3>
                  <RadioGroup
                    value={settings.chatDensity}
                    onValueChange={(value) => {
                      const updatedSettings = {
                        ...settings,
                        chatDensity: value,
                      }
                      setSettings(updatedSettings)
                      saveSettings(updatedSettings)
                      document.documentElement.style.setProperty(
                        "--message-spacing",
                        value === "compact" ? "0.75rem" : "1.5rem",
                      )
                    }}
                  >
                    <div className="space-y-4">
                      {[
                        {
                          value: "comfortable",
                          label: "Comfortable",
                          description: "More space between messages",
                        },
                        {
                          value: "compact",
                          label: "Compact",
                          description: "Less space between messages",
                        },
                      ].map((option) => (
                        <div key={option.value} className="flex items-center space-x-2">
                          <RadioGroupItem value={option.value} id={option.value} />
                          <Label htmlFor={option.value} className="flex flex-col">
                            <span className="text-sm font-medium leading-none dark:text-white">{option.label}</span>
                            <span className="text-sm text-muted-foreground">{option.description}</span>
                          </Label>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>
                </div>
              </div>
            )}

            {activeTab === "notifications" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-4 dark:text-white">Notification Settings</h3>
                  <RadioGroup
                    value={settings.notifications.level}
                    onValueChange={(value) =>
                      updateSettings({
                        notifications: {
                          ...settings.notifications,
                          level: value,
                        },
                      })
                    }
                  >
                    <div className="space-y-4">
                      {[
                        { value: "all", label: "All messages" },
                        { value: "mentions", label: "Only mentions" },
                        { value: "none", label: "Nothing" },
                      ].map((option) => (
                        <div key={option.value} className="flex items-center space-x-2">
                          <RadioGroupItem value={option.value} id={`notification-${option.value}`} />
                          <Label htmlFor={`notification-${option.value}`} className="dark:text-white">
                            {option.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="desktop-notifications" className="dark:text-white">
                      Desktop notifications
                    </Label>
                    <Switch
                      id="desktop-notifications"
                      checked={settings.notifications.desktop}
                      onCheckedChange={(checked) =>
                        updateSettings({
                          notifications: {
                            ...settings.notifications,
                            desktop: checked,
                          },
                        })
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="email-notifications" className="dark:text-white">
                      Email notifications
                    </Label>
                    <Switch
                      id="email-notifications"
                      checked={settings.notifications.email}
                      onCheckedChange={(checked) =>
                        updateSettings({
                          notifications: {
                            ...settings.notifications,
                            email: checked,
                          },
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "sound" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-4 dark:text-white">Sound Settings</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="message-sounds" className="dark:text-white">
                        Message sounds
                      </Label>
                      <Switch
                        id="message-sounds"
                        checked={settings.sound.messageSound}
                        onCheckedChange={(checked) => {
                          updateSettings({
                            sound: {
                              ...settings.sound,
                              messageSound: checked,
                            },
                          })
                          if (checked) {
                            // playSound("message")
                          }
                        }}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label htmlFor="notification-sounds" className="dark:text-white">
                        Notification sounds
                      </Label>
                      <Switch
                        id="notification-sounds"
                        checked={settings.sound.notificationSound}
                        onCheckedChange={(checked) => {
                          updateSettings({
                            sound: {
                              ...settings.sound,
                              notificationSound: checked,
                            },
                          })
                          if (checked) {
                            // playSound("notification")
                          }
                        }}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label htmlFor="call-sounds" className="dark:text-white">
                        Call sounds
                      </Label>
                      <Switch
                        id="call-sounds"
                        checked={settings.sound.callSound}
                        onCheckedChange={(checked) => {
                          updateSettings({
                            sound: {
                              ...settings.sound,
                              callSound: checked,
                            },
                          })
                          if (checked) {
                            // playSound("call")
                          }
                        }}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="volume" className="dark:text-white">
                        Volume
                      </Label>
                      <Slider
                        id="volume"
                        min={0}
                        max={1}
                        step={0.1}
                        value={[settings.sound.volume]}
                        onValueChange={(value) =>
                          updateSettings({
                            sound: {
                              ...settings.sound,
                              volume: value[0],
                            },
                          })
                        }
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "privacy" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-4 dark:text-white">Privacy Settings</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="read-receipts" className="dark:text-white">
                          Read receipts
                        </Label>
                        <p className="text-sm text-muted-foreground">Show when you've read messages</p>
                      </div>
                      <Switch
                        id="read-receipts"
                        checked={settings.privacy.readReceipts}
                        onCheckedChange={(checked) =>
                          updateSettings({
                            privacy: {
                              ...settings.privacy,
                              readReceipts: checked,
                            },
                          })
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="last-seen" className="dark:text-white">
                          Last seen
                        </Label>
                        <p className="text-sm text-muted-foreground">Show when you were last active</p>
                      </div>
                      <Switch
                        id="last-seen"
                        checked={settings.privacy.lastSeen}
                        onCheckedChange={(checked) =>
                          updateSettings({
                            privacy: {
                              ...settings.privacy,
                              lastSeen: checked,
                            },
                          })
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="online-status" className="dark:text-white">
                          Online status
                        </Label>
                        <p className="text-sm text-muted-foreground">Show when you're online</p>
                      </div>
                      <Switch
                        id="online-status"
                        checked={settings.privacy.onlineStatus}
                        onCheckedChange={(checked) =>
                          updateSettings({
                            privacy: {
                              ...settings.privacy,
                              onlineStatus: checked,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

